import { ProductReviewsSection, WishlistButton } from "./Engagement.js";
import {
  designUploadErrorKey,
  type DesignUploadErrorKey,
} from "./design-upload-errors.js";
import { createI18n, directionFor, type Locale } from "@zoya/i18n";
import { ArrowLeftIcon as ArrowLeft } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { ArrowRightIcon as ArrowRight } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { CubeIcon as Cube } from "@phosphor-icons/react/dist/csr/Cube";
import { CheckCircleIcon as CheckCircle } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { FileTextIcon as FileText } from "@phosphor-icons/react/dist/csr/FileText";
import { ImageSquareIcon as ImageSquare } from "@phosphor-icons/react/dist/csr/ImageSquare";
import { LightbulbIcon as Lightbulb } from "@phosphor-icons/react/dist/csr/Lightbulb";
import { PencilLineIcon as PencilLine } from "@phosphor-icons/react/dist/csr/PencilLine";
import { SparkleIcon as Sparkle } from "@phosphor-icons/react/dist/csr/Sparkle";
import { GlobeIcon as Globe } from "@phosphor-icons/react/dist/csr/Globe";
import { MoonIcon as Moon } from "@phosphor-icons/react/dist/csr/Moon";
import { SunIcon as Sun } from "@phosphor-icons/react/dist/csr/Sun";
import { ShoppingBagIcon as ShoppingBag } from "@phosphor-icons/react/dist/csr/ShoppingBag";
import { UploadSimpleIcon as UploadSimple } from "@phosphor-icons/react/dist/csr/UploadSimple";
import { MinusIcon as Minus } from "@phosphor-icons/react/dist/csr/Minus";
import { PlusIcon as Plus } from "@phosphor-icons/react/dist/csr/Plus";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  evaluatePrice,
  acceptCartPrices,
  addAuthenticatedCartItem,
  addCartItem,
  applyCartCoupon,
  checkout,
  getCart,
  getAudienceProducts,
  getAudiences,
  getCatalog,
  getProduct,
  getQuotation,
  prepareAuthenticatedCheckoutCart,
  removeCustomerDesign,
  removeCartCoupon,
  removeCartItem,
  repriceCart,
  submitCustomerDesignRequest,
  submitIdeaRequest,
  updateCartItem,
  uploadCustomerDesign,
  type Cart,
  type CustomerDesignFile,
  type OrderConfirmation,
  type PricingResponse,
  type ShopperAudience,
} from "./api.js";
import { getPaymentInstructions, type PaymentInstructions } from "./api.js";
import {
  AudienceIcon,
  AudiencePicker,
  NO_AUDIENCE,
  readStoredAudience,
  storeAudience,
} from "./AudiencePicker.js";
import { designPreviewKind } from "./design-preview.js";
import {
  AnnouncementReel,
  type AnnouncementReelItem,
} from "./components/ui/announcement-reel.js";
import { CollectionPreview } from "./components/ui/collection-preview.js";
import { CategoryStory } from "./components/ui/category-story.js";
import { JourneyPair } from "./components/ui/journey-pair.js";
import {
  StorefrontFooter,
  type StorefrontFooterSection,
} from "./components/ui/footer-section.js";
import { ZoyaLoader } from "./components/ui/loader.js";
import {
  configurationSummary,
  initialPublishedSelection,
  validatePublishedSelection,
  visibleFields,
  type PublishedField,
  type PublishedSelection,
} from "./configurator.js";
import { messages } from "./messages.js";
import {
  formatMinorCurrency,
  parseCatalogPriceFilter,
  pricePresentation,
} from "./catalog-pricing.js";
import { CustomerAccount } from "./CustomerAccount.js";
import { AboutPage, ContactPage } from "./InformationalPages.js";
import type {
  CatalogSort,
  ProductJourney,
  PublicCatalogProduct,
} from "./types.js";

type Theme = "light" | "dark";
type LoadState<T> =
  { status: "loading" } | { status: "error" } | { status: "ready"; data: T };

const initialLocale = (): Locale =>
  localStorage.getItem("zoya.storefront.locale") === "ar" ||
  (!localStorage.getItem("zoya.storefront.locale") &&
    navigator.language.startsWith("ar"))
    ? "ar"
    : "en";
const initialTheme = (): Theme => {
  const stored = localStorage.getItem("zoya.storefront.theme");
  if (stored === "light" || stored === "dark") return stored;
  return matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const currentPath = () => location.pathname.replace(/\/$/, "") || "/";

function usePath() {
  const [path, setPath] = useState(currentPath);
  useEffect(() => {
    const update = () => setPath(currentPath());
    addEventListener("popstate", update);
    return () => removeEventListener("popstate", update);
  }, []);
  return [
    path,
    (next: string) => {
      history.pushState({}, "", next);
      setPath(currentPath());
      document.querySelector<HTMLElement>("#main-content")?.focus();
      scrollTo({
        top: 0,
        behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "instant"
          : "smooth",
      });
    },
  ] as const;
}

function useCatalog(
  locale: Locale,
  filter?: ProductJourney,
  priceFilter?: { minPriceMinor?: string; maxPriceMinor?: string },
  search?: string,
  sort?: CatalogSort,
) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<LoadState<PublicCatalogProduct[]>>({
    status: "loading",
  });
  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    getCatalog({
      locale,
      ...(filter ? { journey: filter } : {}),
      ...priceFilter,
      ...(search ? { search } : {}),
      ...(sort ? { sort } : {}),
      signal: controller.signal,
    })
      .then((response) => setState({ status: "ready", data: response.data }))
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError"))
          setState({ status: "error" });
      });
    return () => controller.abort();
  }, [
    locale,
    filter,
    priceFilter?.minPriceMinor,
    priceFilter?.maxPriceMinor,
    search,
    sort,
    attempt,
  ]);
  return [state, () => setAttempt((value) => value + 1)] as const;
}

function Link({
  href,
  navigate,
  children,
  className,
  "aria-label": ariaLabel,
}: {
  href: string;
  navigate: (path: string) => void;
  children: React.ReactNode;
  className?: string;
  "aria-label"?: string;
}) {
  return (
    <a
      href={href}
      className={className}
      aria-label={ariaLabel}
      onClick={(event) => {
        if (!event.ctrlKey && !event.metaKey && event.button === 0) {
          event.preventDefault();
          navigate(href);
        }
      }}
    >
      {children}
    </a>
  );
}

const productAction = (
  product: PublicCatalogProduct,
  t: (key: keyof typeof messages.en) => string,
) =>
  product.quoteRequired
    ? t("action.quote")
    : ["CUSTOMIZABLE_PRODUCT", "CUSTOMER_DESIGN"].includes(product.journey)
      ? t("action.configure")
      : product.availability === "TEMPORARILY_UNAVAILABLE"
        ? t("action.unavailable")
        : t("action.view");

const formatFileSize = (byteSize: string, locale: Locale) => {
  const bytes = Number(byteSize);
  const unit = bytes >= 1024 * 1024 ? "megabyte" : "kilobyte";
  const amount =
    unit === "megabyte" ? bytes / (1024 * 1024) : Math.max(1, bytes / 1024);
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    style: "unit",
    unit,
    unitDisplay: "short",
  }).format(amount);
};

const productPriceText = (
  product: PublicCatalogProduct,
  locale: Locale,
  t: (key: keyof typeof messages.en) => string,
) => {
  const pricing = pricePresentation(product);
  if (pricing.kind === "QUOTE_REQUIRED") return t("pricing.requestQuote");
  if (pricing.kind === "UNAVAILABLE") return t("pricing.configureForPrice");
  if (pricing.kind === "FIXED")
    return formatMinorCurrency(locale, pricing.minAmountMinor);
  if (pricing.kind === "FROM")
    return `${t("pricing.from")} ${formatMinorCurrency(locale, pricing.minAmountMinor)}`;
  const maximum = formatMinorCurrency(locale, pricing.maxAmountMinor);
  return `${formatMinorCurrency(locale, pricing.minAmountMinor)}-${
    locale === "en" ? maximum.replace(/^EGP\s/u, "") : maximum
  }`;
};

const productJourneyText = (
  journey: ProductJourney,
  t: (key: keyof typeof messages.en) => string,
) => {
  switch (journey) {
    case "CUSTOMIZABLE_PRODUCT":
      return t("catalog.customizable");
    case "CUSTOMER_DESIGN":
      return t("catalog.customerDesign");
    case "IDEA_TO_PRODUCT":
      return t("catalog.idea");
    default:
      return t("catalog.ready");
  }
};

/**
 * The visitor's shopper audience (Student, Doctor, …). Opens the picker on the
 * first home visit when the store has audiences, and loads the products the
 * admin ranked for the chosen audience.
 */
function useShopperAudience(locale: Locale) {
  const [audiences, setAudiences] = useState<ShopperAudience[]>([]);
  const [selected, setSelected] = useState<string | null>(readStoredAudience);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [products, setProducts] = useState<
    LoadState<PublicCatalogProduct[]> | { status: "none" }
  >({ status: "none" });

  useEffect(() => {
    const controller = new AbortController();
    getAudiences(locale, controller.signal)
      .then((response) => {
        setAudiences(response.data);
        const stored = readStoredAudience();
        const known =
          stored === NO_AUDIENCE ||
          response.data.some((audience) => audience.slug === stored);
        if (!known) setSelected(null);
        if (response.data.length > 0 && !known) setPickerOpen(true);
      })
      .catch(() => {
        // Personalization is optional; the home page works without it.
      });
    return () => controller.abort();
  }, [locale]);

  const active =
    audiences.find((audience) => audience.slug === selected) ?? null;

  useEffect(() => {
    if (!active) {
      setProducts({ status: "none" });
      return;
    }
    const controller = new AbortController();
    setProducts({ status: "loading" });
    getAudienceProducts(active.slug, locale, controller.signal)
      .then((response) => setProducts({ status: "ready", data: response.data }))
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError"))
          setProducts({ status: "error" });
      });
    return () => controller.abort();
  }, [active?.slug, locale]);

  const choose = (slug: string) => {
    storeAudience(slug);
    setSelected(slug);
    setPickerOpen(false);
  };
  return {
    audiences,
    active,
    products,
    pickerOpen,
    openPicker: () => setPickerOpen(true),
    choose,
    dismiss: () =>
      choose(selected && selected !== NO_AUDIENCE ? selected : NO_AUDIENCE),
  };
}

function ProductCard({
  product,
  locale,
  navigate,
  t,
}: {
  product: PublicCatalogProduct;
  locale: Locale;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  const image = product.media.find((item) => item.primary) ?? product.media[0];
  const priceLabel = productPriceText(product, locale, t);
  return (
    <article className="product-card reveal-item">
      <div className="product-card__media-shell">
        <Link
          href={`/products/${product.slug}`}
          navigate={navigate}
          className="product-card__image-link"
          aria-label={`${productAction(product, t)}: ${product.title}`}
        >
          {image ? (
            <img
              src={image.url}
              alt={image.alt}
              width="720"
              height="720"
              loading="lazy"
            />
          ) : (
            <div className="media-fallback" aria-hidden="true">
              <Cube />
              <span>{t("catalog.imagePending")}</span>
            </div>
          )}
        </Link>
      </div>
      <div className="product-card__body">
        <p className="product-card__journey">
          {productJourneyText(product.journey, t)}
        </p>
        <h3>{product.title}</h3>
        <div className="product-card__footer">
          <span className="price">{priceLabel}</span>
          <Link
            href={`/products/${product.slug}`}
            navigate={navigate}
            className="text-link"
          >
            {productAction(product, t)}
            {directionFor(locale) === "rtl" ? (
              <ArrowLeft aria-hidden="true" />
            ) : (
              <ArrowRight aria-hidden="true" />
            )}
          </Link>
        </div>
      </div>
    </article>
  );
}

function ProductGrid({
  state,
  locale,
  navigate,
  retry,
  t,
}: {
  state: LoadState<PublicCatalogProduct[]>;
  locale: Locale;
  navigate: (path: string) => void;
  retry: () => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  if (state.status === "loading")
    return (
      <div className="request-loading-panel" aria-busy="true">
        <ZoyaLoader
          label={t("catalog.loading")}
          className="zoya-request-loader--page"
        />
      </div>
    );
  if (state.status === "error")
    return (
      <div className="state-panel" role="alert">
        <h2>{t("catalog.error")}</h2>
        <button className="button button--dark" onClick={retry}>
          {t("action.retry")}
        </button>
      </div>
    );
  if (state.data.length === 0)
    return (
      <div className="state-panel">
        <h2>{t("catalog.empty")}</h2>
      </div>
    );
  return (
    <div className="product-grid">
      {state.data.map((product) => (
        <ProductCard
          key={product.publicId}
          product={product}
          locale={locale}
          navigate={navigate}
          t={t}
        />
      ))}
    </div>
  );
}

function Home({
  locale,
  navigate,
  t,
}: {
  locale: Locale;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  const [products, retry] = useCatalog(locale);
  const audience = useShopperAudience(locale);
  const personalized =
    audience.active &&
    audience.products.status !== "none" &&
    audience.products.status !== "error" &&
    !(
      audience.products.status === "ready" &&
      audience.products.data.length === 0
    )
      ? (audience.products as LoadState<PublicCatalogProduct[]>)
      : null;
  const reelItems: AnnouncementReelItem[] = [
    {
      id: "workshop-note",
      kind: "announcement",
      label: t("home.reel.announcement.label"),
      title: t("home.reel.announcement.title"),
      description: t("home.reel.announcement.body"),
      image: { src: "/media/zoya-custom-vase.webp", alt: "" },
      href: "/customer-design",
      actionLabel: t("nav.custom"),
    },
    {
      id: "delivery-update",
      kind: "delivery",
      label: t("home.reel.delivery.label"),
      title: t("home.reel.delivery.title"),
      description: t("home.reel.delivery.body"),
      image: { src: "/media/zoya-jewelry-box.webp", alt: "" },
    },
    ...(products.status === "ready"
      ? products.data.map((product) => {
          const image =
            product.media.find((item) => item.primary) ?? product.media[0];
          return {
            id: `product-${product.publicId}`,
            kind: "product" as const,
            label: t("home.reel.product.label"),
            title: product.title,
            description: product.description,
            image: { src: image?.url ?? "", alt: image?.alt ?? "" },
            href: `/products/${product.slug}`,
            actionLabel: t("home.reel.view"),
          };
        })
      : []),
  ];
  return (
    <>
      <div className="hero-shell">
        <section className="hero">
          <div className="hero__copy">
            <p
              className="eyebrow hero-enter"
              style={{ "--delay": "40ms" } as React.CSSProperties}
            >
              {t("hero.eyebrow")}
            </p>
            <h1
              className="hero-enter"
              style={{ "--delay": "100ms" } as React.CSSProperties}
            >
              {t("hero.title")}
            </h1>
            <p
              className="hero__body hero-enter"
              style={{ "--delay": "170ms" } as React.CSSProperties}
            >
              {t("hero.body")}
            </p>
            <Link
              href="/catalog"
              navigate={navigate}
              className="button button--gold hero-enter"
            >
              {t("hero.cta")}
              <span className="button__icon">
                {directionFor(locale) === "rtl" ? (
                  <ArrowLeft />
                ) : (
                  <ArrowRight />
                )}
              </span>
            </Link>
          </div>
          <div
            className="hero__visual hero-enter"
            style={{ "--delay": "80ms" } as React.CSSProperties}
          >
            <img
              src="/media/zoya-hero-olive-board.webp"
              alt=""
              width="1536"
              height="1024"
              fetchPriority="high"
            />
          </div>
        </section>
      </div>

      <AnnouncementReel
        ariaLabel={t("home.reel.label")}
        eyebrow={t("home.reel.eyebrow")}
        title={t("home.reel.title")}
        body={t("home.reel.body")}
        pauseLabel={t("home.reel.pause")}
        resumeLabel={t("home.reel.resume")}
        items={reelItems}
        locale={locale}
        navigate={navigate}
      />

      {audience.pickerOpen && audience.audiences.length > 0 ? (
        <AudiencePicker
          audiences={audience.audiences}
          selected={audience.active?.slug ?? null}
          onChoose={audience.choose}
          onDismiss={audience.dismiss}
          t={t}
        />
      ) : null}

      {audience.audiences.length > 0 ? (
        <div className="audience-bar reveal-item">
          {audience.active ? (
            <p className="audience-bar__current">
              <AudienceIcon
                icon={audience.active.icon}
                className="audience-bar__icon"
              />
              <span>
                {t("audience.current")} <strong>{audience.active.name}</strong>
              </span>
            </p>
          ) : (
            <p className="audience-bar__current">{t("audience.title")}</p>
          )}
          <button
            type="button"
            className="audience-bar__change"
            aria-haspopup="dialog"
            onClick={audience.openPicker}
          >
            {audience.active ? t("audience.change") : t("audience.choose")}
          </button>
        </div>
      ) : null}

      <CollectionPreview
        id="featured"
        title={
          personalized && audience.active
            ? t("home.forAudience").replace("{audience}", audience.active.name)
            : t("home.featured")
        }
        description={
          personalized
            ? t("home.forAudienceBody")
            : audience.active && audience.products.status === "ready"
              ? t("home.forAudienceEmpty")
              : t("home.featuredBody")
        }
      >
        <ProductGrid
          state={
            personalized
              ? personalized
              : products.status === "ready"
                ? { status: "ready", data: products.data.slice(0, 3) }
                : products
          }
          locale={locale}
          navigate={navigate}
          retry={retry}
          t={t}
        />
      </CollectionPreview>

      <CategoryStory id="category" title={t("home.categories")}>
        <div className="category-story__grid">
          <Link
            href="/catalog?journey=READY_PRODUCT"
            navigate={navigate}
            className="category-block category-block--ready"
          >
            <Cube className="category-block__icon" aria-hidden="true" />
            <span>
              <strong>{t("home.ready")}</strong>
              {t("home.readyBody")}
            </span>
            <span className="category-block__cta">
              <ArrowRight aria-hidden="true" />
            </span>
          </Link>
          <Link
            href="/catalog?journey=CUSTOMIZABLE_PRODUCT"
            navigate={navigate}
            className="category-block category-block--custom"
          >
            <PencilLine className="category-block__icon" aria-hidden="true" />
            <span>
              <strong>{t("home.customizable")}</strong>
              {t("home.customizableBody")}
            </span>
            <span className="category-block__cta">
              <ArrowRight aria-hidden="true" />
            </span>
          </Link>
        </div>
      </CategoryStory>

      <section className="craft-story reveal-item">
        <div className="craft-story__image">
          <img
            src="/media/zoya-jewelry-box.webp"
            alt=""
            width="700"
            height="600"
            loading="lazy"
          />
        </div>
        <div className="craft-story__copy">
          <h2>{t("home.story")}</h2>
          <p>{t("home.storyBody")}</p>
        </div>
      </section>

      <JourneyPair ariaLabel={t("journey.boundary")}>
        <Link
          href="/customer-design"
          navigate={navigate}
          className="journey-card journey-card--design reveal-item"
        >
          <div className="journey-card__header">
            <span className="journey-card__tag">01 · {t("nav.custom")}</span>
            <div className="journey-card__icon-box">
              <Sparkle aria-hidden="true" />
            </div>
          </div>
          <div className="journey-card__content">
            <h3>{t("journey.design.title")}</h3>
            <p>{t("journey.design.body")}</p>
          </div>
          <div className="journey-card__footer">
            <span className="journey-card__action-text">{t("nav.custom")}</span>
            <span className="journey-card__arrow">
              {locale === "ar" ? (
                <ArrowLeft aria-hidden="true" />
              ) : (
                <ArrowRight aria-hidden="true" />
              )}
            </span>
          </div>
        </Link>
        <Link
          href="/idea-to-product"
          navigate={navigate}
          className="journey-card journey-card--idea reveal-item"
        >
          <div className="journey-card__header">
            <span className="journey-card__tag">02 · {t("nav.idea")}</span>
            <div className="journey-card__icon-box">
              <Lightbulb aria-hidden="true" />
            </div>
          </div>
          <div className="journey-card__content">
            <h3>{t("journey.idea.title")}</h3>
            <p>{t("journey.idea.body")}</p>
          </div>
          <div className="journey-card__footer">
            <span className="journey-card__action-text">{t("nav.idea")}</span>
            <span className="journey-card__arrow">
              {locale === "ar" ? (
                <ArrowLeft aria-hidden="true" />
              ) : (
                <ArrowRight aria-hidden="true" />
              )}
            </span>
          </div>
        </Link>
      </JourneyPair>
    </>
  );
}

function Catalog({
  locale,
  navigate,
  t,
}: {
  locale: Locale;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  const urlState = () => {
    const query = new URLSearchParams(location.search);
    const value = query.get("journey");
    const journey: ProductJourney | undefined =
      value === "READY_PRODUCT" ||
      value === "CUSTOMIZABLE_PRODUCT" ||
      value === "CUSTOMER_DESIGN" ||
      value === "IDEA_TO_PRODUCT"
        ? value
        : undefined;
    const sortValue = query.get("sort");
    const sort: CatalogSort | undefined =
      sortValue === "newest" ||
      sortValue === "price_asc" ||
      sortValue === "price_desc"
        ? sortValue
        : undefined;
    return {
      journey,
      minEgp: query.get("minPrice") ?? "",
      maxEgp: query.get("maxPrice") ?? "",
      search: query.get("search") ?? "",
      sort,
    };
  };
  const initial = urlState();
  const [filter, setFilter] = useState<ProductJourney | undefined>(
    initial.journey,
  );
  const [appliedPrice, setAppliedPrice] = useState({
    minEgp: initial.minEgp,
    maxEgp: initial.maxEgp,
  });
  const [priceDraft, setPriceDraft] = useState(appliedPrice);
  const [priceError, setPriceError] = useState<
    "PRICE_VALUE_INVALID" | "PRICE_RANGE_INVERTED" | undefined
  >();
  const [appliedSearch, setAppliedSearch] = useState(initial.search);
  const [searchDraft, setSearchDraft] = useState(initial.search);
  const [sort, setSort] = useState<CatalogSort | undefined>(initial.sort);
  useEffect(() => {
    const update = () => {
      const next = urlState();
      setFilter(next.journey);
      const price = { minEgp: next.minEgp, maxEgp: next.maxEgp };
      setAppliedPrice(price);
      setPriceDraft(price);
      setPriceError(undefined);
      setAppliedSearch(next.search);
      setSearchDraft(next.search);
      setSort(next.sort);
    };
    addEventListener("popstate", update);
    return () => removeEventListener("popstate", update);
  }, []);
  const selectFilter = (value: ProductJourney | undefined) => {
    const query = new URLSearchParams(location.search);
    if (value) query.set("journey", value);
    else query.delete("journey");
    history.pushState(
      {},
      "",
      `${location.pathname}${query.size ? `?${query}` : ""}`,
    );
    setFilter(value);
  };
  const parsedPrice = parseCatalogPriceFilter(appliedPrice);
  const [state, retry] = useCatalog(
    locale,
    filter,
    parsedPrice.valid
      ? {
          ...(parsedPrice.minPriceMinor
            ? { minPriceMinor: parsedPrice.minPriceMinor }
            : {}),
          ...(parsedPrice.maxPriceMinor
            ? { maxPriceMinor: parsedPrice.maxPriceMinor }
            : {}),
        }
      : undefined,
    appliedSearch || undefined,
    sort,
  );
  const updatePriceUrl = (next: { minEgp: string; maxEgp: string }) => {
    const query = new URLSearchParams(location.search);
    if (next.minEgp) query.set("minPrice", next.minEgp);
    else query.delete("minPrice");
    if (next.maxEgp) query.set("maxPrice", next.maxEgp);
    else query.delete("maxPrice");
    history.pushState(
      {},
      "",
      `${location.pathname}${query.size ? `?${query}` : ""}`,
    );
    setAppliedPrice(next);
  };
  const updateSearchUrl = (next: string) => {
    const query = new URLSearchParams(location.search);
    if (next) query.set("search", next);
    else query.delete("search");
    history.pushState(
      {},
      "",
      `${location.pathname}${query.size ? `?${query}` : ""}`,
    );
    setAppliedSearch(next);
  };
  const updateSortUrl = (next: CatalogSort | undefined) => {
    const query = new URLSearchParams(location.search);
    if (next && next !== "newest") query.set("sort", next);
    else query.delete("sort");
    history.pushState(
      {},
      "",
      `${location.pathname}${query.size ? `?${query}` : ""}`,
    );
    setSort(next);
  };
  const resultCount =
    state.status === "ready"
      ? `${state.data.length} ${t("catalog.results")}`
      : t("catalog.loading");
  return (
    <section className="catalog-page page section">
      <header className="page-header">
        <p className="eyebrow">{t("catalog.eyebrow")}</p>
        <h1>{t("catalog.title")}</h1>
        <p>{t("catalog.body")}</p>
      </header>
      <div className="catalog-layout">
        <aside className="catalog-sidebar" aria-label={t("catalog.filters")}>
          <div className="catalog-sidebar__heading">
            <h2>{t("catalog.filters")}</h2>
            <p>{t("catalog.filterBody")}</p>
          </div>
          <div className="catalog-filters">
            <form
              className="catalog-search"
              role="search"
              onSubmit={(event) => {
                event.preventDefault();
                updateSearchUrl(searchDraft.trim());
              }}
            >
              <label>
                <span className="visually-hidden">{t("catalog.search")}</span>
                <input
                  name="search"
                  type="search"
                  autoComplete="off"
                  placeholder={t("catalog.searchPlaceholder")}
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                />
              </label>
              <div className="catalog-search__actions">
                <button className="button button--dark" type="submit">
                  {t("catalog.searchButton")}
                </button>
                {appliedSearch ? (
                  <button
                    className="text-button"
                    type="button"
                    onClick={() => {
                      setSearchDraft("");
                      updateSearchUrl("");
                    }}
                  >
                    {t("catalog.clearSearch")}
                  </button>
                ) : null}
              </div>
            </form>
            <div
              className="filter-bar"
              role="group"
              aria-label={t("catalog.typeFilter")}
            >
              {(
                [
                  [undefined, t("catalog.all")],
                  ["READY_PRODUCT", t("catalog.ready")],
                  ["CUSTOMIZABLE_PRODUCT", t("catalog.customizable")],
                  ["CUSTOMER_DESIGN", t("catalog.customerDesign")],
                  ["IDEA_TO_PRODUCT", t("catalog.idea")],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={label}
                  aria-pressed={filter === value}
                  onClick={() => selectFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            <form
              className="price-filter"
              onSubmit={(event) => {
                event.preventDefault();
                const result = parseCatalogPriceFilter(priceDraft);
                if (!result.valid) {
                  setPriceError(result.error);
                  return;
                }
                setPriceError(undefined);
                updatePriceUrl(priceDraft);
              }}
            >
              <fieldset>
                <legend>{t("catalog.price")}</legend>
                <div className="price-filter__inputs">
                  <label>
                    <span>{t("catalog.minPrice")}</span>
                    <input
                      name="minPrice"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={priceDraft.minEgp}
                      aria-invalid={priceError ? "true" : undefined}
                      aria-describedby={
                        priceError ? "price-filter-error" : "price-filter-help"
                      }
                      onChange={(event) =>
                        setPriceDraft((current) => ({
                          ...current,
                          minEgp: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <span className="price-filter__separator" aria-hidden="true">
                    -
                  </span>
                  <label>
                    <span>{t("catalog.maxPrice")}</span>
                    <input
                      name="maxPrice"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={priceDraft.maxEgp}
                      aria-invalid={priceError ? "true" : undefined}
                      aria-describedby={
                        priceError ? "price-filter-error" : "price-filter-help"
                      }
                      onChange={(event) =>
                        setPriceDraft((current) => ({
                          ...current,
                          maxEgp: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <p id="price-filter-help" className="filter-help">
                  {t("catalog.priceHelp")}
                </p>
                {priceError ? (
                  <p
                    id="price-filter-error"
                    className="validation-message"
                    role="alert"
                  >
                    {t(
                      priceError === "PRICE_RANGE_INVERTED"
                        ? "catalog.priceInverted"
                        : "catalog.priceInvalid",
                    )}
                  </p>
                ) : null}
                <div className="price-filter__actions">
                  <button className="button button--dark" type="submit">
                    {t("catalog.applyPrice")}
                  </button>
                  <button
                    className="text-button"
                    type="button"
                    onClick={() => {
                      const cleared = { minEgp: "", maxEgp: "" };
                      setPriceDraft(cleared);
                      setPriceError(undefined);
                      updatePriceUrl(cleared);
                    }}
                  >
                    {t("catalog.clearPrice")}
                  </button>
                </div>
              </fieldset>
            </form>
          </div>
        </aside>
        <div className="catalog-results">
          <header className="catalog-results__header">
            <h2>{t("catalog.resultsTitle")}</h2>
            <p className="catalog-results__count" aria-live="polite">
              {resultCount}
            </p>
            <label className="catalog-sort">
              <span>{t("catalog.sort")}</span>
              <select
                name="sort"
                value={sort ?? "newest"}
                onChange={(event) =>
                  updateSortUrl(event.target.value as CatalogSort)
                }
              >
                <option value="newest">{t("catalog.sortNewest")}</option>
                <option value="price_asc">{t("catalog.sortPriceAsc")}</option>
                <option value="price_desc">{t("catalog.sortPriceDesc")}</option>
              </select>
            </label>
          </header>
          <ProductGrid
            state={state}
            locale={locale}
            navigate={navigate}
            retry={retry}
            t={t}
          />
        </div>
      </div>
    </section>
  );
}

function ConfigurationControl({
  field,
  value,
  locale,
  onChange,
  t,
}: {
  field: PublishedField;
  value: string | number | boolean | undefined;
  locale: Locale;
  onChange: (value: string | number | boolean) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  if (field.options?.length)
    return (
      <fieldset className="config-field config-field--options">
        <legend>
          {field.label[locale]}{" "}
          {field.required ? <span>{t("product.required")}</span> : null}
        </legend>
        {field.helpText ? <p>{field.helpText[locale]}</p> : null}
        <div className="option-grid">
          {field.options.map((option) => (
            <label
              key={option.key}
              data-selected={value === option.key || undefined}
            >
              <input
                type="radio"
                name={field.key}
                value={option.key}
                checked={value === option.key}
                onChange={() => onChange(option.key)}
              />
              <span
                className={
                  field.type === "COLOR"
                    ? `swatch swatch--${option.key}`
                    : undefined
                }
                aria-hidden={field.type === "COLOR" ? "true" : undefined}
              />
              {option.label[locale]}
            </label>
          ))}
        </div>
      </fieldset>
    );
  return (
    <label className="config-field">
      <span>
        {field.label[locale]}{" "}
        {field.required ? <small>{t("product.required")}</small> : null}
      </span>
      {field.helpText ? <small>{field.helpText[locale]}</small> : null}
      <input
        type={
          field.type === "NUMBER" ||
          field.type === "DIMENSIONS" ||
          field.type === "QUANTITY"
            ? "number"
            : "text"
        }
        name={field.key}
        autoComplete="off"
        inputMode={
          field.type === "NUMBER" ||
          field.type === "DIMENSIONS" ||
          field.type === "QUANTITY"
            ? "decimal"
            : "text"
        }
        value={value === undefined ? "" : String(value)}
        min={field.validation?.min}
        max={field.validation?.max}
        onChange={(event) =>
          onChange(
            field.type === "NUMBER" ||
              field.type === "DIMENSIONS" ||
              field.type === "QUANTITY"
              ? Number(event.target.value)
              : event.target.value,
          )
        }
      />
    </label>
  );
}

function Configurator({
  product,
  locale,
  navigate,
  t,
}: {
  product: PublicCatalogProduct;
  locale: Locale;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  const fields = product.configuration.fields;
  const [selection, setSelection] = useState<PublishedSelection>(() =>
    initialPublishedSelection(fields),
  );
  const [quantity, setQuantity] = useState(1);
  const [designFile, setDesignFile] = useState<CustomerDesignFile | null>(null);
  const [designNotes, setDesignNotes] = useState("");
  const [uploadState, setUploadState] = useState<
    "idle" | "uploading" | "removing" | "uploaded" | "error"
  >("idle");
  const [uploadErrorKey, setUploadErrorKey] = useState<DesignUploadErrorKey>(
    "customerDesign.error",
  );
  const designInput = useRef<HTMLInputElement>(null);
  const [cartAction, setCartAction] = useState<"idle" | "adding" | "error">(
    "idle",
  );
  const [price, setPrice] = useState<
    | { status: "idle" | "loading" | "error" }
    | { status: "ready"; data: PricingResponse }
  >({ status: "idle" });
  const priceRequest = useRef(0);
  const designBusy = uploadState === "uploading" || uploadState === "removing";
  const errors = validatePublishedSelection(fields, selection);
  const summary = configurationSummary(fields, selection, locale);
  useEffect(() => {
    if (errors.length) {
      priceRequest.current += 1;
      setPrice({ status: "idle" });
      return;
    }
    const requestId = ++priceRequest.current;
    const controller = new AbortController();
    setPrice({ status: "loading" });
    const timer = setTimeout(() => {
      evaluatePrice({
        slug: product.slug,
        quantity,
        configuration: selection,
        signal: controller.signal,
      })
        .then((data) => {
          if (priceRequest.current === requestId)
            setPrice({ status: "ready", data });
        })
        .catch((error: unknown) => {
          if (
            priceRequest.current === requestId &&
            !(error instanceof DOMException && error.name === "AbortError")
          )
            setPrice({ status: "error" });
        });
    }, 280);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [errors.length, product.slug, quantity, selection]);
  return (
    <section className="configurator" aria-labelledby="config-title">
      <div className="configurator__controls">
        <h2 id="config-title">{t("product.configuration")}</h2>
        <p>{t("product.configurationBody")}</p>
        {visibleFields(fields, selection).map((field) => (
          <ConfigurationControl
            key={field.key}
            field={field}
            value={selection[field.key]}
            locale={locale}
            t={t}
            onChange={(value) =>
              setSelection((current) => ({ ...current, [field.key]: value }))
            }
          />
        ))}
        {product.journey === "CUSTOMER_DESIGN" ? (
          <section
            className="customer-design-input"
            aria-labelledby="customer-design-title"
          >
            <div className="customer-design-input__heading">
              <div>
                <p className="eyebrow">{t("customerDesign.eyebrow")}</p>
                <h3 id="customer-design-title">{t("customerDesign.title")}</h3>
              </div>
              <button
                className="button button--dark"
                type="button"
                aria-describedby="customer-design-policy customer-design-status"
                disabled={designBusy}
                onClick={() => designInput.current?.click()}
              >
                {t(
                  designFile
                    ? "customerDesign.replace"
                    : "customerDesign.upload",
                )}
              </button>
            </div>
            <input
              ref={designInput}
              id="customer-design-file"
              hidden
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.pdf,.svg,.stl,.obj,.3mf,.step,.stp,image/png,image/jpeg,image/webp,application/pdf,image/svg+xml,model/stl,model/obj,model/3mf,model/step"
              aria-describedby="customer-design-policy customer-design-status"
              disabled={designBusy}
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                setUploadState("uploading");
                try {
                  const uploaded = await uploadCustomerDesign(file);
                  setDesignFile(uploaded);
                  setUploadState("uploaded");
                } catch (error) {
                  if (
                    error instanceof Error &&
                    error.message === "CUSTOMER_SESSION_INVALID"
                  ) {
                    navigate(
                      `/login?returnTo=${encodeURIComponent(`/products/${product.slug}`)}`,
                    );
                    return;
                  }
                  setUploadErrorKey(designUploadErrorKey(error));
                  setUploadState("error");
                }
              }}
            />
            <p
              id="customer-design-policy"
              className="customer-design-input__policy"
            >
              {t("customerDesign.policy")}
            </p>
            <div
              id="customer-design-status"
              className="customer-design-input__status"
              aria-live="polite"
              aria-busy={designBusy}
            >
              {uploadState === "idle" ? (
                <p>{t("customerDesign.empty")}</p>
              ) : null}
              {uploadState === "uploading" ? (
                <ZoyaLoader size={24} label={t("customerDesign.uploading")} />
              ) : null}
              {uploadState === "removing" ? (
                <ZoyaLoader size={24} label={t("customerDesign.removing")} />
              ) : null}
              {uploadState === "error" ? (
                <p role="alert">{t(uploadErrorKey)}</p>
              ) : null}
              {designFile ? (
                <div className="customer-design-file">
                  <div>
                    <strong>
                      <bdi dir="ltr">{designFile.displayName}</bdi>
                    </strong>
                    <span>
                      {designFile.mediaType} ·{" "}
                      {formatFileSize(designFile.byteSize, locale)}
                    </span>
                    <small>{t("customerDesign.uploaded")}</small>
                  </div>
                  <button
                    className="text-button"
                    type="button"
                    disabled={designBusy}
                    onClick={async () => {
                      setUploadState("removing");
                      try {
                        await removeCustomerDesign(
                          designFile.attachmentPublicId,
                        );
                        setDesignFile(null);
                        setUploadState("idle");
                        if (designInput.current) designInput.current.value = "";
                      } catch {
                        setUploadErrorKey("customerDesign.error");
                        setUploadState("error");
                      }
                    }}
                    aria-label={`${t("customerDesign.remove")}: ${designFile.displayName}`}
                  >
                    {t("customerDesign.remove")}
                  </button>
                </div>
              ) : null}
            </div>
            <label className="config-field" htmlFor="customer-design-notes">
              <span>{t("customerDesign.notes")}</span>
              <small>{t("customerDesign.notesHelp")}</small>
              <textarea
                id="customer-design-notes"
                value={designNotes}
                maxLength={2_000}
                rows={5}
                onChange={(event) => setDesignNotes(event.target.value)}
              />
            </label>
          </section>
        ) : null}
        <label className="config-field">
          <span>{t("pricing.quantity")}</span>
          <input
            type="number"
            min="1"
            max="10000"
            step="1"
            value={quantity}
            onChange={(event) =>
              setQuantity(
                Math.max(
                  1,
                  Math.min(10_000, Math.trunc(Number(event.target.value)) || 1),
                ),
              )
            }
          />
        </label>
      </div>
      <aside className="configuration-summary" aria-live="polite">
        <h3>{t("product.summary")}</h3>
        {summary.length ? (
          <dl>
            {summary.map((item) => (
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p>{t("product.select")}</p>
        )}
        {errors.length ? (
          <p className="validation-message">{t("product.validation")}</p>
        ) : null}
        <div
          className="authoritative-price"
          aria-live="polite"
          aria-busy={price.status === "loading"}
        >
          <span>{t("pricing.currentConfiguration")}</span>
          {price.status === "idle" ? <p>{t("pricing.complete")}</p> : null}
          {price.status === "loading" ? (
            <ZoyaLoader size={24} label={t("pricing.loading")} />
          ) : null}
          {price.status === "error" ? (
            <p role="alert">{t("pricing.error")}</p>
          ) : null}
          {price.status === "ready" &&
          price.data.outcome === "QUOTE_REQUIRED" ? (
            <p className="quote-required">{t("pricing.quoteRequired")}</p>
          ) : null}
          {price.status === "ready" && price.data.outcome === "PRICED" ? (
            <>
              <strong>
                {formatMinorCurrency(locale, price.data.totalAmountMinor, true)}
              </strong>
              <details>
                <summary>{t("pricing.breakdown")}</summary>
                <dl>
                  {price.data.breakdown.map((item) => (
                    <div key={item.code}>
                      <dt>{item.label[locale]}</dt>
                      <dd>
                        {formatMinorCurrency(locale, item.amountMinor, true)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </details>
            </>
          ) : null}
        </div>
        {price.status === "ready" && price.data.outcome === "PRICED" ? (
          <button
            className="button button--gold"
            disabled={
              errors.length > 0 ||
              cartAction === "adding" ||
              (product.journey === "CUSTOMER_DESIGN" &&
                (!designFile || designBusy))
            }
            onClick={async () => {
              setCartAction("adding");
              try {
                await addAuthenticatedCartItem({
                  slug: product.slug,
                  quantity,
                  configuration: selection,
                  ...(designFile
                    ? {
                        customerDesign: {
                          attachmentPublicId: designFile.attachmentPublicId,
                          notes: designNotes,
                        },
                      }
                    : {}),
                });
                navigate("/cart");
              } catch (error) {
                if (
                  error instanceof Error &&
                  error.message === "CUSTOMER_SESSION_INVALID"
                ) {
                  navigate(
                    `/login?returnTo=${encodeURIComponent(`/products/${product.slug}`)}`,
                  );
                  return;
                }
                setCartAction("error");
              }
            }}
          >
            {cartAction === "adding" ? (
              <ZoyaLoader size={20} label={t("cart.adding")} />
            ) : (
              t("cart.add")
            )}
          </button>
        ) : null}
        {cartAction === "error" ? (
          <p className="validation-message" role="alert">
            {t("cart.error")}
          </p>
        ) : null}
      </aside>
    </section>
  );
}

function QuotationPage({
  publicId,
  locale,
  t,
}: {
  publicId: string;
  locale: Locale;
  t: (key: keyof typeof messages.en) => string;
}) {
  const [secret, setSecret] = useState("");
  const [state, setState] = useState<
    | { status: "idle" | "loading" | "error" }
    | { status: "ready"; data: Record<string, unknown> }
  >({ status: "idle" });
  const load = () => {
    const controller = new AbortController();
    setState({ status: "loading" });
    getQuotation(publicId, secret, controller.signal)
      .then((data) => setState({ status: "ready", data }))
      .catch(() => setState({ status: "error" }));
  };
  if (state.status !== "ready")
    return (
      <section className="quotation-page page section">
        <header className="page-header">
          <p className="eyebrow">{t("quotation.eyebrow")}</p>
          <h1>{t("quotation.accessTitle")}</h1>
          <p>{t("quotation.accessBody")}</p>
        </header>
        <label className="config-field">
          <span>{t("quotation.accessCode")}</span>
          <input
            value={secret}
            autoComplete="one-time-code"
            onChange={(event) => setSecret(event.target.value)}
          />
        </label>
        <button
          className="button button--gold"
          disabled={secret.length < 12 || state.status === "loading"}
          onClick={load}
        >
          {state.status === "loading" ? (
            <ZoyaLoader size={20} label={t("quotation.loading")} />
          ) : (
            t("quotation.open")
          )}
        </button>
        {state.status === "error" ? (
          <p role="alert">{t("quotation.notFound")}</p>
        ) : null}
      </section>
    );
  const data = state.data as {
    status: "ISSUED" | "EXPIRED";
    quotationVersion: number;
    totalAmountMinor: string;
    issuedAt: string;
    expiresAt: string;
    terms: Record<Locale, string>;
    lines: Array<{
      publicId: string;
      productContent: Record<Locale, { title: string }>;
      quantity: number;
      configurationLabels: Array<{
        key: string;
        label: Record<Locale, string>;
        value: Record<Locale, string>;
      }>;
      breakdown: Array<{
        code: string;
        label: Record<Locale, string>;
        amountMinor: string;
      }>;
    }>;
  };
  return (
    <article className="quotation-page page section">
      <header className="quotation-heading">
        <div>
          <p className="eyebrow">{t("quotation.eyebrow")}</p>
          <h1>{t("quotation.title")}</h1>
          <p>
            {t("quotation.version")} {data.quotationVersion}
          </p>
        </div>
        <span className="quotation-status">
          {t(
            `quotation.status.${data.status.toLowerCase()}` as keyof typeof messages.en,
          )}
        </span>
      </header>
      <dl className="quotation-dates">
        <div>
          <dt>{t("quotation.issued")}</dt>
          <dd>
            {new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(
              new Date(data.issuedAt),
            )}
          </dd>
        </div>
        <div>
          <dt>{t("quotation.expires")}</dt>
          <dd>
            {new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(
              new Date(data.expiresAt),
            )}
          </dd>
        </div>
      </dl>
      {data.lines.map((line) => (
        <section className="quotation-line" key={line.publicId}>
          <div>
            <h2>{line.productContent[locale].title}</h2>
            <p>
              {t("pricing.quantity")}: {line.quantity}
            </p>
            {line.configurationLabels.map((item) => (
              <p key={item.key}>
                {item.label[locale]}: {item.value[locale]}
              </p>
            ))}
          </div>
          <dl>
            {line.breakdown.map((item) => (
              <div key={item.code}>
                <dt>{item.label[locale]}</dt>
                <dd>{formatMinorCurrency(locale, item.amountMinor, true)}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
      <div className="quotation-total">
        <span>{t("quotation.total")}</span>
        <strong>
          {formatMinorCurrency(locale, data.totalAmountMinor, true)}
        </strong>
      </div>
      <section className="quotation-terms">
        <h2>{t("quotation.terms")}</h2>
        <p>{data.terms[locale]}</p>
      </section>
    </article>
  );
}

function ProductPage({
  slug,
  locale,
  navigate,
  t,
}: {
  slug: string;
  locale: Locale;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<LoadState<PublicCatalogProduct>>({
    status: "loading",
  });
  const [mediaIndex, setMediaIndex] = useState(0);
  const [cartAction, setCartAction] = useState<"idle" | "adding" | "error">(
    "idle",
  );
  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    getProduct(slug, locale, controller.signal)
      .then((data) => setState({ status: "ready", data }))
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError"))
          setState({ status: "error" });
      });
    return () => controller.abort();
  }, [slug, locale, attempt]);
  if (state.status === "loading")
    return (
      <div className="page section request-loading-panel" aria-busy="true">
        <ZoyaLoader
          label={t("product.loading")}
          className="zoya-request-loader--page"
        />
      </div>
    );
  if (state.status === "error")
    return (
      <div className="page section state-panel" role="alert">
        <h1>{t("catalog.error")}</h1>
        <button
          className="button button--dark"
          onClick={() => setAttempt((value) => value + 1)}
        >
          {t("action.retry")}
        </button>
      </div>
    );
  const product = state.data;
  const media = product.media[mediaIndex] ?? product.media[0];
  return (
    <article className="product-page page section">
      <Link href="/catalog" navigate={navigate} className="back-link">
        <ArrowLeft aria-hidden="true" />
        {t("product.back")}
      </Link>
      <div className="product-detail">
        <section className="gallery" aria-label={t("product.gallery")}>
          <div className="gallery__primary">
            {media ? (
              <img
                key={media.publicId}
                src={media.url}
                alt={media.alt}
                width="1024"
                height="1024"
              />
            ) : (
              <div className="media-fallback" />
            )}
            {product.media.length > 1 ? (
              <div className="gallery__arrows">
                <button
                  aria-label={t("product.previous")}
                  onClick={() =>
                    setMediaIndex(
                      (mediaIndex - 1 + product.media.length) %
                        product.media.length,
                    )
                  }
                >
                  <ArrowLeft />
                </button>
                <button
                  aria-label={t("product.next")}
                  onClick={() =>
                    setMediaIndex((mediaIndex + 1) % product.media.length)
                  }
                >
                  <ArrowRight />
                </button>
              </div>
            ) : null}
          </div>
          {product.media.length > 1 ? (
            <div className="gallery__thumbs">
              {product.media.map((item, index) => (
                <button
                  key={item.publicId}
                  aria-label={item.alt}
                  aria-pressed={index === mediaIndex}
                  onClick={() => setMediaIndex(index)}
                >
                  <img src={item.url} alt="" width="120" height="120" />
                </button>
              ))}
            </div>
          ) : null}
        </section>
        <section className="product-copy">
          <p className="eyebrow">
            {product.journey === "CUSTOMIZABLE_PRODUCT"
              ? t("catalog.customizable")
              : product.journey === "CUSTOMER_DESIGN"
                ? t("customerDesign.eyebrow")
                : t("catalog.ready")}
          </p>
          <h1>{product.title}</h1>
          <p className="product-description">{product.description}</p>
          <WishlistButton
            slug={product.slug}
            locale={locale}
            navigate={navigate}
          />
          {product.pricingEnvelope ? (
            <div className="price-block">
              <span>{t("product.publishedPrice")}</span>
              <strong>{productPriceText(product, locale, t)}</strong>
              <small>{t("product.priceBoundary")}</small>
            </div>
          ) : null}
          {!["CUSTOMIZABLE_PRODUCT", "CUSTOMER_DESIGN"].includes(
            product.journey,
          ) ? (
            <div className="scope-action">
              <button
                className="button button--gold"
                disabled={
                  product.journey !== "READY_PRODUCT" ||
                  product.availability !== "PURCHASABLE" ||
                  cartAction === "adding"
                }
                onClick={async () => {
                  setCartAction("adding");
                  try {
                    await addCartItem({
                      slug: product.slug,
                      quantity: 1,
                      configuration: {},
                    });
                    navigate("/cart");
                  } catch {
                    setCartAction("error");
                  }
                }}
              >
                {cartAction === "adding" ? (
                  <ZoyaLoader size={20} label={t("cart.adding")} />
                ) : product.journey === "READY_PRODUCT" ? (
                  t("cart.add")
                ) : (
                  productAction(product, t)
                )}
              </button>
              <p>
                {product.journey === "READY_PRODUCT"
                  ? t("product.readyPurchase")
                  : t("product.guestReadyOnly")}
              </p>
              {cartAction === "error" ? (
                <p role="alert">{t("cart.error")}</p>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>
      {["CUSTOMIZABLE_PRODUCT", "CUSTOMER_DESIGN"].includes(product.journey) ? (
        <Configurator
          key={`${product.publication.id}:${product.publication.version}`}
          product={product}
          locale={locale}
          navigate={navigate}
          t={t}
        />
      ) : null}
      <ProductReviewsSection
        slug={product.slug}
        locale={locale}
        navigate={navigate}
      />
    </article>
  );
}

const couponErrorKey = (
  reason: string | undefined,
): keyof typeof messages.en => {
  switch (reason) {
    case "COUPON_NOT_FOUND":
      return "cart.coupon.error.COUPON_NOT_FOUND";
    case "COUPON_CODE_INVALID":
      return "cart.coupon.error.COUPON_CODE_INVALID";
    case "COUPON_INACTIVE":
      return "cart.coupon.error.COUPON_INACTIVE";
    case "COUPON_NOT_STARTED":
      return "cart.coupon.error.COUPON_NOT_STARTED";
    case "COUPON_EXPIRED":
      return "cart.coupon.error.COUPON_EXPIRED";
    case "COUPON_BELOW_MINIMUM_SUBTOTAL":
      return "cart.coupon.error.COUPON_BELOW_MINIMUM_SUBTOTAL";
    case "COUPON_REDEMPTION_LIMIT_REACHED":
      return "cart.coupon.error.COUPON_REDEMPTION_LIMIT_REACHED";
    case "COUPON_CUSTOMER_LIMIT_REACHED":
      return "cart.coupon.error.COUPON_CUSTOMER_LIMIT_REACHED";
    case "COUPON_NO_LONGER_VALID":
      return "cart.coupon.error.COUPON_NO_LONGER_VALID";
    default:
      return "cart.coupon.error.generic";
  }
};

function CartPage({
  locale,
  navigate,
  t,
}: {
  locale: Locale;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  const [state, setState] = useState<LoadState<Cart>>({ status: "loading" });
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState<string | null>(null);
  const load = () => {
    setState({ status: "loading" });
    getCart()
      .then((data) => setState({ status: "ready", data }))
      .catch(() => setState({ status: "error" }));
  };
  const mutateCart = async (action: string, request: () => Promise<Cart>) => {
    if (pendingAction) return;
    setPendingAction(action);
    try {
      setState({ status: "ready", data: await request() });
    } catch {
      setState({ status: "error" });
    } finally {
      setPendingAction(null);
    }
  };
  const submitCoupon = async () => {
    if (pendingAction || !couponInput.trim()) return;
    setPendingAction("apply-coupon");
    setCouponError(null);
    try {
      const data = await applyCartCoupon(couponInput.trim());
      setState({ status: "ready", data });
      setCouponInput("");
    } catch (error) {
      setCouponError(
        error instanceof Error ? error.message : "COUPON_NOT_FOUND",
      );
    } finally {
      setPendingAction(null);
    }
  };
  useEffect(() => {
    load();
  }, []);
  if (state.status === "loading")
    return (
      <section className="page section request-loading-panel" aria-busy="true">
        <ZoyaLoader
          label={t("cart.loading")}
          className="zoya-request-loader--page"
        />
      </section>
    );
  if (state.status === "error")
    return (
      <section className="page section state-panel" role="alert">
        <h1>{t("cart.error")}</h1>
        <button className="button button--dark" onClick={load}>
          {t("action.retry")}
        </button>
      </section>
    );
  const cart = state.data;
  return (
    <section
      className="commerce-page page section"
      aria-labelledby="cart-title"
    >
      <header className="commerce-heading">
        <p className="eyebrow">{t("cart.eyebrow")}</p>
        <h1 id="cart-title">{t("cart.title")}</h1>
        <p aria-live="polite">
          {cart.lineCount} {t("cart.items")}
        </p>
      </header>
      {cart.items.length === 0 ? (
        <div className="state-panel">
          <h2>{t("cart.empty")}</h2>
          <Link
            href="/catalog"
            navigate={navigate}
            className="button button--dark"
          >
            {t("cart.shop")}
          </Link>
        </div>
      ) : (
        <div className="cart-layout">
          <ul className="cart-list">
            {cart.items.map((item) => (
              <li className="cart-line" key={item.publicId}>
                <div className="cart-line__image">
                  {item.image ? (
                    <img src={item.image} alt="" width="160" height="160" />
                  ) : null}
                </div>
                <div className="cart-line__copy">
                  <h2>
                    <Link href={`/products/${item.slug}`} navigate={navigate}>
                      {item.title}
                    </Link>
                  </h2>
                  <p>
                    {item.priceState === "CHANGED" ? (
                      <>
                        <s>
                          {formatMinorCurrency(
                            locale,
                            item.unitAmountMinor,
                            true,
                          )}
                        </s>{" "}
                        <strong>
                          {formatMinorCurrency(
                            locale,
                            item.candidateUnitAmountMinor!,
                            true,
                          )}
                        </strong>
                        <small className="price-change">
                          {t("cart.priceChanged")}
                        </small>
                      </>
                    ) : (
                      formatMinorCurrency(locale, item.unitAmountMinor, true)
                    )}
                  </p>
                  {item.customerDesign ? (
                    <div className="cart-line__design">
                      <strong>{t("customerDesign.title")}</strong>
                      <span>
                        <bdi dir="ltr">{item.customerDesign.displayName}</bdi>
                      </span>
                      {item.customerDesign.notes ? (
                        <p>{item.customerDesign.notes}</p>
                      ) : null}
                    </div>
                  ) : null}
                  <div
                    className="quantity-control"
                    role="group"
                    aria-label={`${t("cart.quantity")} ${item.title}`}
                  >
                    <button
                      aria-label={t("cart.decrease")}
                      disabled={item.quantity === 1 || pendingAction !== null}
                      onClick={() =>
                        void mutateCart(`decrease:${item.publicId}`, () =>
                          updateCartItem(item.publicId, item.quantity - 1),
                        )
                      }
                    >
                      {pendingAction === `decrease:${item.publicId}` ? (
                        <ZoyaLoader
                          size={18}
                          label={t("cart.updating")}
                          showLabel={false}
                        />
                      ) : (
                        <Minus />
                      )}
                    </button>
                    <output aria-live="polite">{item.quantity}</output>
                    <button
                      aria-label={t("cart.increase")}
                      disabled={pendingAction !== null}
                      onClick={() =>
                        void mutateCart(`increase:${item.publicId}`, () =>
                          updateCartItem(item.publicId, item.quantity + 1),
                        )
                      }
                    >
                      {pendingAction === `increase:${item.publicId}` ? (
                        <ZoyaLoader
                          size={18}
                          label={t("cart.updating")}
                          showLabel={false}
                        />
                      ) : (
                        <Plus />
                      )}
                    </button>
                  </div>
                  <button
                    className="text-button"
                    disabled={pendingAction !== null}
                    onClick={() =>
                      void mutateCart(`remove:${item.publicId}`, () =>
                        removeCartItem(item.publicId),
                      )
                    }
                  >
                    {pendingAction === `remove:${item.publicId}` ? (
                      <ZoyaLoader size={18} label={t("cart.removing")} />
                    ) : (
                      t("cart.remove")
                    )}
                  </button>
                </div>
                <strong>
                  {formatMinorCurrency(locale, item.lineAmountMinor, true)}
                </strong>
              </li>
            ))}
          </ul>
          <aside className="order-summary">
            <h2>{t("cart.summary")}</h2>
            <div>
              <span>{t("cart.subtotal")}</span>
              <strong>
                {formatMinorCurrency(locale, cart.subtotalAmountMinor, true)}
              </strong>
            </div>
            {cart.coupon?.valid ? (
              <div>
                <span>
                  {t("cart.coupon.discount")} · {cart.coupon.code}
                </span>
                <strong>
                  −
                  {formatMinorCurrency(
                    locale,
                    cart.coupon.discountAmountMinor,
                    true,
                  )}
                </strong>
              </div>
            ) : null}
            {cart.coupon?.valid && cart.totalAmountMinor ? (
              <div>
                <span>{t("cart.coupon.total")}</span>
                <strong>
                  {formatMinorCurrency(locale, cart.totalAmountMinor, true)}
                </strong>
              </div>
            ) : null}
            <div className="cart-coupon">
              {cart.coupon ? (
                <>
                  {!cart.coupon.valid ? (
                    <p className="cart-coupon__error" role="alert">
                      {cart.coupon.code} —{" "}
                      {t(couponErrorKey(cart.coupon.reason))}
                    </p>
                  ) : null}
                  <button
                    className="text-button"
                    disabled={pendingAction !== null}
                    onClick={() =>
                      void mutateCart("remove-coupon", () => removeCartCoupon())
                    }
                  >
                    {pendingAction === "remove-coupon" ? (
                      <ZoyaLoader size={18} label={t("cart.coupon.removing")} />
                    ) : (
                      `${t("cart.coupon.remove")} (${cart.coupon.code})`
                    )}
                  </button>
                </>
              ) : (
                <>
                  <label htmlFor="cart-coupon-input">
                    {t("cart.coupon.label")}
                  </label>
                  <div className="cart-coupon__row">
                    <input
                      id="cart-coupon-input"
                      type="text"
                      value={couponInput}
                      placeholder={t("cart.coupon.placeholder")}
                      disabled={pendingAction !== null}
                      onChange={(event) => {
                        setCouponInput(event.target.value);
                        setCouponError(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") void submitCoupon();
                      }}
                    />
                    <button
                      className="text-button"
                      disabled={pendingAction !== null || !couponInput.trim()}
                      onClick={() => void submitCoupon()}
                    >
                      {pendingAction === "apply-coupon" ? (
                        <ZoyaLoader
                          size={18}
                          label={t("cart.coupon.applying")}
                        />
                      ) : (
                        t("cart.coupon.apply")
                      )}
                    </button>
                  </div>
                  {couponError ? (
                    <p className="cart-coupon__error" role="alert">
                      {t(couponErrorKey(couponError))}
                    </p>
                  ) : null}
                </>
              )}
            </div>
            <button
              className="text-button"
              disabled={pendingAction !== null}
              onClick={() => void mutateCart("reprice", () => repriceCart())}
            >
              {pendingAction === "reprice" ? (
                <ZoyaLoader size={18} label={t("cart.repricing")} />
              ) : (
                t("cart.reprice")
              )}
            </button>
            {cart.items.some((item) => item.priceState === "CHANGED") ? (
              <button
                className="button button--dark"
                disabled={pendingAction !== null}
                onClick={() =>
                  void mutateCart("accept-prices", () => acceptCartPrices())
                }
              >
                {pendingAction === "accept-prices" ? (
                  <ZoyaLoader size={18} label={t("cart.updating")} />
                ) : (
                  t("cart.acceptPrices")
                )}
              </button>
            ) : null}
            <p>{t("cart.deliveryLater")}</p>
            {cart.items.some((item) => item.priceState === "CHANGED") ? (
              <button className="button button--gold" disabled>
                {t("cart.acceptToCheckout")}
              </button>
            ) : (
              <Link
                href="/checkout"
                navigate={navigate}
                className="button button--gold"
              >
                {t("cart.checkout")}
              </Link>
            )}
          </aside>
        </div>
      )}
    </section>
  );
}

/** Where and how to pay for cash, bank transfer and InstaPay orders. */
function PaymentHelp({
  method,
  orderNumber,
  t,
}: {
  method: string;
  orderNumber?: string;
  t: (key: keyof typeof messages.en) => string;
}) {
  const [instructions, setInstructions] = useState<PaymentInstructions | null>(
    null,
  );
  useEffect(() => {
    getPaymentInstructions()
      .then(setInstructions)
      .catch(() => setInstructions({ bankTransfer: null, instapay: null }));
  }, []);
  if (method === "CASH")
    return (
      <div className="payment-help" role="note">
        <p>{t("paymentHelp.cash")}</p>
      </div>
    );
  if (method !== "BANK_TRANSFER" && method !== "INSTAPAY") return null;
  const bank = method === "BANK_TRANSFER" ? instructions?.bankTransfer : null;
  const instapay = method === "INSTAPAY" ? instructions?.instapay : null;
  return (
    <div className="payment-help" role="note">
      <h3>{t("paymentHelp.title")}</h3>
      {instructions === null ? null : bank ? (
        <>
          <p>{t("paymentHelp.bank")}</p>
          <dl>
            <div>
              <dt>{t("paymentHelp.bankName")}</dt>
              <dd>{bank.bankName}</dd>
            </div>
            <div>
              <dt>{t("paymentHelp.accountName")}</dt>
              <dd>{bank.accountName}</dd>
            </div>
            <div>
              <dt>{t("paymentHelp.accountNumber")}</dt>
              <dd dir="ltr">{bank.accountNumber}</dd>
            </div>
            {orderNumber ? (
              <div>
                <dt>{t("paymentHelp.reference")}</dt>
                <dd dir="ltr">{orderNumber}</dd>
              </div>
            ) : null}
          </dl>
        </>
      ) : instapay ? (
        <>
          <p>{t("paymentHelp.instapay")}</p>
          <dl>
            <div>
              <dt>{t("paymentHelp.instapayAddress")}</dt>
              <dd dir="ltr">{instapay.address}</dd>
            </div>
            {orderNumber ? (
              <div>
                <dt>{t("paymentHelp.reference")}</dt>
                <dd dir="ltr">{orderNumber}</dd>
              </div>
            ) : null}
          </dl>
        </>
      ) : (
        <p>{t("paymentHelp.missing")}</p>
      )}
      <p>{t("paymentHelp.confirm")}</p>
    </div>
  );
}

function CheckoutPage({
  locale,
  navigate,
  t,
}: {
  locale: Locale;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [delivery, setDelivery] = useState<"PICKUP" | "COURIER">("PICKUP");
  const [payment, setPayment] = useState("COD");
  const [addressLine, setAddressLine] = useState("");
  const [city, setCity] = useState("Cairo");
  const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [order, setOrder] = useState<OrderConfirmation | null>(null);
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    prepareAuthenticatedCheckoutCart()
      .then(({ customer, cart }) => {
        setEmail(customer.email);
        setName(`${customer.firstName} ${customer.lastName}`.trim());
        setCart(cart);
      })
      .catch((error: unknown) => {
        if (
          error instanceof Error &&
          error.message === "CUSTOMER_SESSION_INVALID"
        )
          navigate("/login?returnTo=/checkout");
        else
          getCart()
            .then(setCart)
            .catch(() => navigate("/cart"));
      });
  }, []);
  if (order)
    return (
      <section className="confirmation-page page section" role="status">
        <p className="eyebrow">{t("confirmation.eyebrow")}</p>
        <h1>{t("confirmation.title")}</h1>
        <p>{t("confirmation.body")}</p>
        <dl>
          <div>
            <dt>{t("confirmation.number")}</dt>
            <dd>{order.orderNumber}</dd>
          </div>
          <div>
            <dt>{t("confirmation.total")}</dt>
            <dd>{formatMinorCurrency(locale, order.totalAmountMinor, true)}</dd>
          </div>
          <div>
            <dt>{t("confirmation.payment")}</dt>
            <dd>
              {t(
                `payment.${order.payment.method.toLowerCase()}` as keyof typeof messages.en,
              )}
            </dd>
          </div>
          <div>
            <dt>{t("confirmation.delivery")}</dt>
            <dd>
              {t(
                `delivery.${order.fulfillment.mode.toLowerCase()}` as keyof typeof messages.en,
              )}
            </dd>
          </div>
        </dl>
        <PaymentHelp
          method={order.payment.method}
          orderNumber={order.orderNumber}
          t={t}
        />
        <p>{t("confirmation.next")}</p>
        <div className="confirmation-actions">
          <Link
            href="/account/orders"
            navigate={navigate}
            className="button button--gold"
          >
            {t("confirmation.orders")}
          </Link>
          <Link href="/catalog" navigate={navigate} className="text-link">
            {t("confirmation.continue")}
          </Link>
        </div>
      </section>
    );
  if (!cart)
    return (
      <section className="page section request-loading-panel" aria-busy="true">
        <ZoyaLoader
          label={t("checkout.loading")}
          className="zoya-request-loader--page"
        />
      </section>
    );
  return (
    <section
      className="commerce-page page section"
      aria-labelledby="checkout-title"
    >
      <header className="commerce-heading">
        <p className="eyebrow">{t("checkout.eyebrow")}</p>
        <h1 id="checkout-title">{t("checkout.title")}</h1>
        <p>{t("checkout.body")}</p>
      </header>
      <nav className="checkout-progress" aria-label={t("checkout.progress")}>
        <ol>
          {[
            t("checkout.contact"),
            t("checkout.delivery"),
            t("checkout.payment"),
            t("checkout.review"),
          ].map((label, index) => (
            <li key={label}>
              <span aria-hidden="true">{index + 1}</span>
              <strong>{label}</strong>
            </li>
          ))}
        </ol>
      </nav>
      {status === "error" ? (
        <div
          className="error-summary checkout-error"
          role="alert"
          ref={errorSummaryRef}
          tabIndex={-1}
        >
          <div>
            <strong>{t("checkout.errorTitle")}</strong>
            <p>
              {checkoutError === "STOCK_UNAVAILABLE"
                ? t("checkout.stockUnavailable")
                : checkoutError === "CART_PRICE_CHANGED"
                  ? t("checkout.priceChanged")
                  : checkoutError === "CONTACT_NOT_VERIFIED"
                    ? t("checkout.contactNotVerified")
                    : t("checkout.error")}
            </p>
          </div>
          <Link href="/cart" navigate={navigate} className="text-link">
            {t("checkout.returnToCart")}
          </Link>
        </div>
      ) : null}
      <form
        className="checkout-layout"
        onSubmit={async (event) => {
          event.preventDefault();
          setStatus("submitting");
          setCheckoutError(null);
          try {
            const idempotencyKey = crypto.randomUUID().replaceAll("-", "");
            setOrder(
              await checkout(
                {
                  cartRevision: cart.revision,
                  contact: { email, name },
                  delivery:
                    delivery === "PICKUP"
                      ? { mode: delivery, pickupLocation: "zoya-cairo" }
                      : {
                          mode: delivery,
                          address: {
                            city,
                            line1: addressLine,
                          },
                        },
                  paymentMethod: payment,
                },
                idempotencyKey,
              ),
            );
          } catch (error) {
            setCheckoutError(
              error instanceof Error ? error.message : "CHECKOUT_FAILED",
            );
            setStatus("error");
            requestAnimationFrame(() => errorSummaryRef.current?.focus());
          }
        }}
      >
        <div className="checkout-sections">
          <fieldset id="checkout-contact" disabled={status === "submitting"}>
            <legend>
              <span className="checkout-step-number" aria-hidden="true">
                1
              </span>
              <span>{t("checkout.contact")}</span>
            </legend>
            <label>
              <span>{t("checkout.name")}</span>
              <input
                required
                maxLength={100}
                autoComplete="name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label>
              <span>{t("checkout.email")}</span>
              <input
                required
                type="email"
                maxLength={254}
                autoComplete="email"
                value={email}
                readOnly
                aria-describedby="checkout-verified-email"
              />
              <small id="checkout-verified-email">
                {t("checkout.verifiedEmail")}
              </small>
            </label>
          </fieldset>
          <fieldset disabled={status === "submitting"}>
            <legend>
              <span className="checkout-step-number" aria-hidden="true">
                2
              </span>
              <span>{t("checkout.delivery")}</span>
            </legend>
            {(["PICKUP", "COURIER"] as const).map((mode) => (
              <label className="choice-card" key={mode}>
                <input
                  type="radio"
                  name="delivery"
                  value={mode}
                  checked={delivery === mode}
                  onChange={() => setDelivery(mode)}
                />
                <span>
                  <strong>
                    {t(
                      `delivery.${mode.toLowerCase()}` as keyof typeof messages.en,
                    )}
                  </strong>
                  <small>
                    {t(
                      `delivery.${mode.toLowerCase()}Body` as keyof typeof messages.en,
                    )}
                  </small>
                </span>
              </label>
            ))}
            {delivery === "COURIER" ? (
              <div className="checkout-address-fields">
                <label>
                  <span>{t("checkout.addressLine")}</span>
                  <input
                    required
                    name="addressLine"
                    autoComplete="address-line1"
                    maxLength={240}
                    value={addressLine}
                    onChange={(event) => setAddressLine(event.target.value)}
                  />
                </label>
                <label>
                  <span>{t("checkout.city")}</span>
                  <input
                    required
                    name="city"
                    autoComplete="address-level2"
                    maxLength={120}
                    value={city}
                    onChange={(event) => setCity(event.target.value)}
                  />
                </label>
              </div>
            ) : null}
          </fieldset>
          <fieldset disabled={status === "submitting"}>
            <legend>
              <span className="checkout-step-number" aria-hidden="true">
                3
              </span>
              <span>{t("checkout.payment")}</span>
            </legend>
            {["COD", "CASH", "BANK_TRANSFER", "INSTAPAY"].map((method) => (
              <label className="choice-card" key={method}>
                <input
                  type="radio"
                  name="payment"
                  value={method}
                  checked={payment === method}
                  onChange={() => setPayment(method)}
                />
                <span>
                  <strong>
                    {t(
                      `payment.${method.toLowerCase()}` as keyof typeof messages.en,
                    )}
                  </strong>
                </span>
              </label>
            ))}
            <PaymentHelp method={payment} t={t} />
          </fieldset>
        </div>
        <aside className="order-summary" aria-label={t("cart.summary")}>
          <div className="order-summary__heading">
            <div>
              <span className="checkout-step-number" aria-hidden="true">
                4
              </span>
              <h2>{t("checkout.review")}</h2>
            </div>
            <Link href="/cart" navigate={navigate} className="text-link">
              {t("checkout.editCart")}
            </Link>
          </div>
          <ul className="checkout-summary__items">
            {cart.items.map((item) => (
              <li key={item.publicId}>
                <div className="checkout-summary__image">
                  {item.image ? <img src={item.image} alt="" /> : null}
                </div>
                <div>
                  <strong>{item.title}</strong>
                  <span>
                    {t("checkout.quantityShort")} {item.quantity}
                  </span>
                </div>
                <strong>
                  {formatMinorCurrency(locale, item.lineAmountMinor, true)}
                </strong>
              </li>
            ))}
          </ul>
          <div className="order-summary__total">
            <span>{t("cart.subtotal")}</span>
            <strong>
              {formatMinorCurrency(locale, cart.subtotalAmountMinor, true)}
            </strong>
          </div>
          <dl className="checkout-summary__choices">
            <div>
              <dt>{t("checkout.delivery")}</dt>
              <dd>
                {t(
                  `delivery.${delivery.toLowerCase()}` as keyof typeof messages.en,
                )}
              </dd>
            </div>
            <div>
              <dt>{t("checkout.payment")}</dt>
              <dd>
                {t(
                  `payment.${payment.toLowerCase()}` as keyof typeof messages.en,
                )}
              </dd>
            </div>
          </dl>
          <p>{t("checkout.authoritative")}</p>
          <button
            className="button button--gold"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? (
              <ZoyaLoader size={20} label={t("checkout.submitting")} />
            ) : (
              t("checkout.place")
            )}
          </button>
        </aside>
      </form>
    </section>
  );
}

function JourneyPage({
  kind,
  locale,
  navigate,
  t,
}: {
  kind: "design" | "idea";
  locale: Locale;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  if (kind === "design")
    return <CustomerDesignJourney locale={locale} navigate={navigate} t={t} />;
  return <IdeaJourney locale={locale} navigate={navigate} t={t} />;
}

function CustomerDesignJourney({
  locale,
  navigate,
  t,
}: {
  locale: Locale;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  return (
    <section className="journey-page page section">
      <div className="journey-page__hero">
        <div className="journey-page__copy">
          <p className="eyebrow">{t("nav.custom")}</p>
          <h1>{t("journey.design.title")}</h1>
          <p>{t("journey.design.body")}</p>
          <div className="boundary-note">{t("designRequest.boundary")}</div>
        </div>
        <img
          src="/media/zoya-custom-vase.webp"
          alt=""
          width="1024"
          height="1024"
        />
      </div>
      <CustomerDesignRequestForm locale={locale} navigate={navigate} t={t} />
    </section>
  );
}

function IdeaJourney({
  locale,
  navigate,
  t,
}: {
  locale: Locale;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  const [products, retry] = useCatalog(locale, "IDEA_TO_PRODUCT");
  const ideaOffering =
    products.status === "ready" ? (products.data[0] ?? null) : null;
  return (
    <section className="journey-page page section">
      <div className="journey-page__hero">
        <div className="journey-page__copy">
          <p className="eyebrow">{t("nav.idea")}</p>
          <h1>{t("journey.idea.title")}</h1>
          <p>{t("journey.idea.body")}</p>
          <div className="boundary-note">{t("journey.boundary")}</div>
          <Link
            href="/catalog?journey=IDEA_TO_PRODUCT"
            navigate={navigate}
            className="button button--gold"
            aria-label={t("journey.idea.cta")}
          >
            {t("journey.idea.cta")}
            <span className="button__icon" aria-hidden="true">
              {locale === "ar" ? <ArrowLeft /> : <ArrowRight />}
            </span>
          </Link>
        </div>
        <img
          src="/media/zoya-hero-olive-board.webp"
          alt=""
          width="1024"
          height="1024"
        />
      </div>
      <IdeaRequestForm
        offering={ideaOffering}
        navigate={navigate}
        t={t}
        locale={locale}
      />
      <section
        className="journey-page__offerings"
        aria-labelledby="idea-offerings-title"
      >
        <header className="section-heading">
          <h2 id="idea-offerings-title">{t("journey.offerings")}</h2>
          <p>{t("journey.offeringsBody")}</p>
        </header>
        <ProductGrid
          state={products}
          locale={locale}
          navigate={navigate}
          retry={retry}
          t={t}
        />
      </section>
    </section>
  );
}

function CustomerDesignRequestForm({
  locale,
  navigate,
  t,
}: {
  locale: Locale;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  const [designFile, setDesignFile] = useState<CustomerDesignFile | null>(null);
  const [uploadState, setUploadState] = useState<
    "idle" | "uploading" | "removing" | "uploaded" | "error"
  >("idle");
  const [uploadErrorKey, setUploadErrorKey] = useState<DesignUploadErrorKey>(
    "customerDesign.error",
  );
  const [status, setStatus] = useState<
    "idle" | "submitting" | "done" | "error"
  >("idle");
  const [designPreviewUrl, setDesignPreviewUrl] = useState<string | null>(null);
  const [reference, setReference] = useState("");
  const [error, setError] = useState("");
  const designInput = useRef<HTMLInputElement>(null);
  const busy = uploadState === "uploading" || uploadState === "removing";
  useEffect(() => {
    if (!designPreviewUrl) return;
    return () => URL.revokeObjectURL(designPreviewUrl);
  }, [designPreviewUrl]);
  const requireLogin = () => {
    sessionStorage.setItem("zoya.storefront.returnTo", "/customer-design");
    navigate("/account/login");
  };
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!designFile || busy) return;
    setStatus("submitting");
    setError("");
    const form = event.currentTarget;
    const data = new FormData(form);
    try {
      const result = await submitCustomerDesignRequest(
        {
          attachmentPublicId: designFile.attachmentPublicId,
          title: String(data.get("title") ?? ""),
          notes: String(data.get("notes") ?? ""),
          quantity: Number(data.get("quantity") ?? 1),
          ...(String(data.get("targetDate") ?? "")
            ? {
                targetDate: `${String(data.get("targetDate"))}T12:00:00.000Z`,
              }
            : {}),
        },
        `design-request-${crypto.randomUUID()}`,
      );
      setReference(result.requestPublicId);
      setStatus("done");
      setDesignFile(null);
      setDesignPreviewUrl(null);
      setUploadState("idle");
      form.reset();
      if (designInput.current) designInput.current.value = "";
    } catch (cause) {
      const code =
        cause instanceof Error ? cause.message : "SERVICE_UNAVAILABLE";
      if (code.includes("CUSTOMER_SESSION")) return requireLogin();
      setError(code);
      setStatus("error");
    }
  };
  const previewKind = designFile
    ? designPreviewKind(designFile.mediaType, designFile.displayName)
    : "file";
  return (
    <section className="design-request" aria-labelledby="design-request-title">
      <div className="design-request__intro">
        <p className="eyebrow">{t("designRequest.eyebrow")}</p>
        <h2 id="design-request-title">{t("designRequest.title")}</h2>
        <p>{t("designRequest.body")}</p>
        <ol>
          <li>{t("designRequest.step1")}</li>
          <li>{t("designRequest.step2")}</li>
          <li>{t("designRequest.step3")}</li>
        </ol>
      </div>
      <form className="design-request__form" onSubmit={submit}>
        <label>
          {t("designRequest.projectTitle")}
          <input name="title" minLength={3} maxLength={160} required />
        </label>
        <section className="customer-design-input">
          <div className="customer-design-input__heading">
            <div>
              <p className="customer-design-input__eyebrow">
                <Sparkle size={15} weight="fill" aria-hidden="true" />
                {t("customerDesign.previewLabel")}
              </p>
              <strong>{t("customerDesign.title")}</strong>
              <p id="standalone-design-policy">{t("customerDesign.policy")}</p>
            </div>
            <span className="customer-design-input__step">
              {t("customerDesign.step")}
            </span>
          </div>
          <div className={`customer-design-studio is-${previewKind}`}>
            <div
              className="design-preview-avatar"
              aria-label={
                designFile
                  ? `${designFile.displayName} ${t("customerDesign.previewReady")}`
                  : t("customerDesign.previewWaiting")
              }
              role="img"
            >
              {designPreviewUrl && previewKind === "image" ? (
                <img
                  src={designPreviewUrl}
                  alt=""
                  className="design-preview-avatar__image"
                />
              ) : (
                <span
                  className="design-preview-avatar__fallback"
                  aria-hidden="true"
                >
                  {previewKind === "model" ? (
                    <Cube size={34} weight="duotone" />
                  ) : previewKind === "document" ? (
                    <FileText size={34} weight="duotone" />
                  ) : previewKind === "image" ? (
                    <ImageSquare size={34} weight="duotone" />
                  ) : (
                    <Sparkle size={34} weight="duotone" />
                  )}
                </span>
              )}
              <span
                className="design-preview-avatar__orbit"
                aria-hidden="true"
              />
            </div>
            <div className="customer-design-studio__copy">
              <p className="customer-design-studio__label">
                {designFile
                  ? t("customerDesign.previewReady")
                  : t("customerDesign.previewLabel")}
              </p>
              <p className="customer-design-studio__hint">
                {designFile
                  ? designFile.displayName
                  : t("customerDesign.previewWaiting")}
              </p>
              {!designFile ? (
                <p className="customer-design-studio__detail">
                  {t("customerDesign.previewHint")}
                </p>
              ) : null}
            </div>
            <button
              className="button button--dark customer-design-input__action"
              type="button"
              disabled={busy}
              onClick={() => designInput.current?.click()}
            >
              <UploadSimple size={17} weight="bold" aria-hidden="true" />
              {t(
                designFile ? "customerDesign.replace" : "customerDesign.upload",
              )}
            </button>
          </div>
          <input
            ref={designInput}
            hidden
            type="file"
            aria-label={t("customerDesign.upload")}
            aria-describedby="standalone-design-policy standalone-design-status"
            accept=".png,.jpg,.jpeg,.webp,.pdf,.svg,.stl,.obj,.3mf,.step,.stp,image/png,image/jpeg,image/webp,application/pdf,image/svg+xml,model/stl,model/obj,model/3mf,model/step"
            disabled={busy}
            onChange={async (event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              setUploadState("uploading");
              const nextPreviewUrl = file.type.startsWith("image/")
                ? URL.createObjectURL(file)
                : null;
              try {
                const previous = designFile;
                const uploaded = await uploadCustomerDesign(file);
                setDesignFile(uploaded);
                setDesignPreviewUrl(nextPreviewUrl);
                setUploadState("uploaded");
                if (previous)
                  await removeCustomerDesign(previous.attachmentPublicId).catch(
                    () => undefined,
                  );
              } catch (cause) {
                if (nextPreviewUrl) URL.revokeObjectURL(nextPreviewUrl);
                if (
                  cause instanceof Error &&
                  cause.message === "CUSTOMER_SESSION_INVALID"
                )
                  return requireLogin();
                setUploadErrorKey(designUploadErrorKey(cause));
                setUploadState("error");
              }
            }}
          />
          <div
            id="standalone-design-status"
            className="customer-design-input__status"
            aria-live="polite"
            aria-busy={busy}
          >
            {uploadState === "idle" ? <p>{t("customerDesign.empty")}</p> : null}
            {uploadState === "uploading" ? (
              <ZoyaLoader size={24} label={t("customerDesign.uploading")} />
            ) : null}
            {uploadState === "removing" ? (
              <ZoyaLoader size={24} label={t("customerDesign.removing")} />
            ) : null}
            {uploadState === "error" ? (
              <p role="alert">{t(uploadErrorKey)}</p>
            ) : null}
            {designFile ? (
              <div className="customer-design-file">
                <div>
                  <strong>
                    <bdi dir="ltr">{designFile.displayName}</bdi>
                  </strong>
                  <span>
                    {designFile.mediaType} ·{" "}
                    {formatFileSize(designFile.byteSize, locale)}
                  </span>
                  <small className="customer-design-file__ready">
                    <CheckCircle size={15} weight="fill" aria-hidden="true" />
                    {t("customerDesign.uploaded")}
                  </small>
                </div>
                <button
                  className="text-button"
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    setUploadState("removing");
                    try {
                      await removeCustomerDesign(designFile.attachmentPublicId);
                      setDesignFile(null);
                      setDesignPreviewUrl(null);
                      setUploadState("idle");
                      if (designInput.current) designInput.current.value = "";
                    } catch {
                      setUploadErrorKey("customerDesign.error");
                      setUploadState("error");
                    }
                  }}
                >
                  {t("customerDesign.remove")}
                </button>
              </div>
            ) : null}
          </div>
        </section>
        <label>
          {t("customerDesign.notes")}
          <textarea name="notes" maxLength={2_000} rows={5} />
          <small>{t("customerDesign.notesHelp")}</small>
        </label>
        <div className="design-request__row">
          <label>
            {t("designRequest.quantity")}
            <input
              name="quantity"
              type="number"
              min="1"
              max="10000"
              defaultValue="1"
              required
            />
          </label>
          <label>
            {t("designRequest.targetDate")}
            <input name="targetDate" type="date" />
          </label>
        </div>
        <p className="design-request__note">
          {t("designRequest.quoteBoundary")}
        </p>
        {status === "done" ? (
          <p className="design-request__success" role="status">
            {t("designRequest.success")} <strong>{reference}</strong>{" "}
            <a href="/account/quotations">
              {locale === "ar" ? "متابعة عروض الأسعار" : "View your quotations"}
            </a>
          </p>
        ) : null}
        {status === "error" ? (
          <p className="form-error" role="alert">
            {t("designRequest.error")} ({error})
          </p>
        ) : null}
        <button
          className="button button--gold"
          disabled={!designFile || busy || status === "submitting"}
        >
          {status === "submitting" ? (
            <ZoyaLoader size={20} label={t("designRequest.submitting")} />
          ) : (
            t("designRequest.submit")
          )}
        </button>
      </form>
    </section>
  );
}

function IdeaRequestForm({
  offering,
  navigate,
  t,
  locale,
}: {
  offering: PublicCatalogProduct | null;
  navigate: (path: string) => void;
  t: (key: keyof typeof messages.en) => string;
  locale: Locale;
}) {
  const [status, setStatus] = useState<
    "idle" | "submitting" | "done" | "error"
  >("idle");
  const [reference, setReference] = useState("");
  const [error, setError] = useState("");
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!offering) return;
    setStatus("submitting");
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      const budget = String(data.get("budget") ?? "").trim();
      const result = await submitIdeaRequest(
        {
          slug: offering.slug,
          title: String(data.get("title") ?? ""),
          description: String(data.get("description") ?? ""),
          quantity: Number(data.get("quantity") ?? 1),
          ...(String(data.get("targetDate") ?? "")
            ? { targetDate: `${String(data.get("targetDate"))}T12:00:00.000Z` }
            : {}),
          ...(budget
            ? { budgetAmountMinor: String(Math.round(Number(budget) * 100)) }
            : {}),
        },
        `idea-${crypto.randomUUID()}`,
      );
      setReference(result.requestPublicId);
      setStatus("done");
      event.currentTarget.reset();
    } catch (cause) {
      const code =
        cause instanceof Error ? cause.message : "SERVICE_UNAVAILABLE";
      if (code.includes("CUSTOMER_SESSION")) {
        sessionStorage.setItem("zoya.storefront.returnTo", "/idea-to-product");
        navigate("/account/login");
        return;
      }
      setError(code);
      setStatus("error");
    }
  };
  return (
    <section className="idea-brief" aria-labelledby="idea-brief-title">
      <div className="idea-brief__intro">
        <p className="eyebrow">{t("ideaRequest.eyebrow")}</p>
        <h2 id="idea-brief-title">{t("ideaRequest.title")}</h2>
        <p>{t("ideaRequest.body")}</p>
        <ol>
          <li>{t("ideaRequest.step1")}</li>
          <li>{t("ideaRequest.step2")}</li>
          <li>{t("ideaRequest.step3")}</li>
        </ol>
      </div>
      <form className="idea-brief__form" onSubmit={submit}>
        <label>
          {t("ideaRequest.projectTitle")}
          <input name="title" minLength={3} maxLength={160} required />
        </label>
        <label>
          {t("ideaRequest.description")}
          <textarea
            name="description"
            minLength={20}
            maxLength={5000}
            rows={6}
            required
          />
        </label>
        <div className="idea-brief__row">
          <label>
            {t("ideaRequest.quantity")}
            <input
              name="quantity"
              type="number"
              min="1"
              max="10000"
              defaultValue="1"
              required
            />
          </label>
          <label>
            {t("ideaRequest.targetDate")}
            <input name="targetDate" type="date" />
          </label>
          <label>
            {t("ideaRequest.budget")}
            <input
              name="budget"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
            />
          </label>
        </div>
        <p className="idea-brief__note">{t("ideaRequest.quoteBoundary")}</p>
        {status === "done" ? (
          <p className="idea-brief__success" role="status">
            {t("ideaRequest.success")} <strong>{reference}</strong>{" "}
            <a href="/account/quotations">
              {locale === "ar" ? "متابعة عروض الأسعار" : "View your quotations"}
            </a>
          </p>
        ) : null}
        {status === "error" ? (
          <p className="form-error" role="alert">
            {t("ideaRequest.error")} ({error})
          </p>
        ) : null}
        <button
          className="button button--gold"
          disabled={!offering || status === "submitting"}
        >
          {!offering ? (
            t("ideaRequest.unavailable")
          ) : status === "submitting" ? (
            <ZoyaLoader size={20} label={t("ideaRequest.submitting")} />
          ) : (
            t("ideaRequest.submit")
          )}
        </button>
      </form>
    </section>
  );
}

export function App() {
  const [locale, setLocale] = useState<Locale>(initialLocale);
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const mobileMenu = useRef<HTMLDivElement>(null);
  const [path, navigateRaw] = usePath();
  const i18n = useMemo(() => createI18n(locale, messages), [locale]);
  const t = i18n.t as (key: keyof typeof messages.en) => string;
  const navigate = (next: string) => {
    setMenuOpen(false);
    navigateRaw(next);
  };
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = directionFor(locale);
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("zoya.storefront.locale", locale);
    localStorage.setItem("zoya.storefront.theme", theme);
  }, [locale, theme]);
  useEffect(() => {
    const title = path.startsWith("/products/")
      ? "Zoya Hub | Product"
      : path === "/catalog"
        ? `Zoya Hub | ${t("nav.shop")}`
        : path === "/about"
          ? `Zoya Hub | ${t("nav.about")}`
          : path === "/contact"
            ? `Zoya Hub | ${t("nav.contact")}`
            : `Zoya Hub | ${t("brand.tagline")}`;
    document.title = title;
  }, [path, t]);
  useEffect(() => {
    if (!menuOpen) return;
    mobileMenu.current?.querySelector<HTMLElement>("a")?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      menuButton.current?.focus();
    };
    addEventListener("keydown", closeOnEscape);
    return () => removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);
  useEffect(() => {
    const items = new Set<Element>();
    const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      document
        .querySelectorAll(".reveal-item")
        .forEach((item) => item.setAttribute("data-visible", "true"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute("data-visible", "true");
          observer.unobserve(entry.target);
          items.delete(entry.target);
        });
      },
      { threshold: 0.14 },
    );
    const observeNewItems = () => {
      document
        .querySelectorAll(".reveal-item:not([data-visible])")
        .forEach((item) => {
          if (items.has(item)) return;
          items.add(item);
          observer.observe(item);
        });
    };
    observeNewItems();
    const mutations = new MutationObserver(observeNewItems);
    mutations.observe(document.body, { childList: true, subtree: true });
    return () => {
      mutations.disconnect();
      observer.disconnect();
    };
  }, [path]);
  const productSlug = path.startsWith("/products/")
    ? decodeURIComponent(path.slice("/products/".length))
    : null;
  const quotationId = path.startsWith("/quotations/")
    ? decodeURIComponent(path.slice("/quotations/".length))
    : null;
  const footerSections: StorefrontFooterSection[] = [
    {
      label: t("footer.browse"),
      links: [
        { title: t("footer.collection"), href: "/catalog" },
        {
          title: t("home.ready"),
          href: "/catalog?journey=READY_PRODUCT",
        },
        {
          title: t("home.customizable"),
          href: "/catalog?journey=CUSTOMIZABLE_PRODUCT",
        },
      ],
    },
    {
      label: t("footer.create"),
      links: [
        { title: t("nav.custom"), href: "/customer-design" },
        { title: t("nav.idea"), href: "/idea-to-product" },
      ],
    },
    {
      label: t("footer.company"),
      links: [
        { title: t("footer.about"), href: "/about" },
        { title: t("footer.contact"), href: "/contact" },
      ],
    },
  ];
  return (
    <div className="site-shell" data-menu-open={menuOpen || undefined}>
      <a className="skip-link" href="#main-content">
        {t("a11y.skip")}
      </a>
      <header className="site-header">
        <div className="nav-shell">
          <Link href="/" navigate={navigate} className="brand">
            <img
              className="brand__logo"
              src="/brand/zoya-hub-lockup.webp"
              alt="Zoya Hub"
              width="77"
              height="54"
            />
            <span>
              <small>{t("brand.tagline")}</small>
            </span>
          </Link>
          <nav className="desktop-nav" aria-label={t("nav.shop")}>
            <Link href="/" navigate={navigate}>
              {t("nav.home")}
            </Link>
            <Link href="/catalog" navigate={navigate}>
              {t("nav.shop")}
            </Link>
            <Link href="/customer-design" navigate={navigate}>
              {t("nav.custom")}
            </Link>
            <Link href="/idea-to-product" navigate={navigate}>
              {t("nav.idea")}
            </Link>
            <Link href="/about" navigate={navigate}>
              {t("nav.about")}
            </Link>
            <Link href="/contact" navigate={navigate}>
              {t("nav.contact")}
            </Link>
            <Link href="/account" navigate={navigate}>
              {locale === "ar" ? "حسابي" : "Account"}
            </Link>
          </nav>
          <div className="nav-actions">
            <Link
              href="/cart"
              navigate={navigate}
              className="icon-button"
              aria-label={t("nav.cart")}
            >
              <ShoppingBag aria-hidden="true" />
            </Link>
            <button
              className="icon-button"
              aria-label={t("nav.locale")}
              onClick={() => setLocale(locale === "en" ? "ar" : "en")}
            >
              <Globe aria-hidden="true" />
            </button>
            <button
              className="icon-button"
              aria-label={t("nav.theme")}
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            >
              {theme === "light" ? (
                <Moon aria-hidden="true" />
              ) : (
                <Sun aria-hidden="true" />
              )}
            </button>
            <button
              ref={menuButton}
              className="icon-button menu-button"
              aria-label={menuOpen ? t("nav.close") : t("nav.open")}
              aria-controls="mobile-navigation"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((value) => !value)}
            >
              <span className="menu-glyph" aria-hidden="true">
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>
      </header>
      <div
        ref={mobileMenu}
        id="mobile-navigation"
        className="mobile-menu"
        aria-hidden={!menuOpen}
        inert={!menuOpen}
      >
        <nav>
          {(
            [
              ["/", "nav.home"],
              ["/catalog", "nav.shop"],
              ["/customer-design", "nav.custom"],
              ["/idea-to-product", "nav.idea"],
              ["/about", "nav.about"],
              ["/contact", "nav.contact"],
              ["/account", locale === "ar" ? "حسابي" : "Account"],
            ] satisfies ReadonlyArray<readonly [string, string]>
          ).map(([href, key]) => (
            <Link
              key={href}
              href={href!}
              navigate={navigate}
              className="mobile-menu__link"
            >
              {key.includes(".") ? t(key as keyof typeof messages.en) : key}
            </Link>
          ))}
          <button
            type="button"
            className="mobile-menu__link mobile-menu__action"
            onClick={() => {
              setLocale(locale === "en" ? "ar" : "en");
              setMenuOpen(false);
            }}
          >
            {t("nav.locale")}
          </button>
        </nav>
      </div>
      <main id="main-content" tabIndex={-1} className="route-view" key={path}>
        {path === "/" ? (
          <Home locale={locale} navigate={navigate} t={t} />
        ) : path === "/catalog" ? (
          <Catalog locale={locale} navigate={navigate} t={t} />
        ) : path === "/cart" ? (
          <CartPage locale={locale} navigate={navigate} t={t} />
        ) : path === "/checkout" ? (
          <CheckoutPage locale={locale} navigate={navigate} t={t} />
        ) : path === "/login" ||
          path === "/register" ||
          path === "/forgot-password" ||
          path === "/reset-password" ||
          path === "/verify-email" ||
          path === "/account" ||
          path.startsWith("/account/") ? (
          <CustomerAccount locale={locale} path={path} navigate={navigate} />
        ) : quotationId ? (
          <QuotationPage publicId={quotationId} locale={locale} t={t} />
        ) : productSlug ? (
          <ProductPage
            slug={productSlug}
            locale={locale}
            navigate={navigate}
            t={t}
          />
        ) : path === "/customer-design" ? (
          <JourneyPage
            kind="design"
            locale={locale}
            navigate={navigate}
            t={t}
          />
        ) : path === "/idea-to-product" ? (
          <JourneyPage kind="idea" locale={locale} navigate={navigate} t={t} />
        ) : path === "/about" ? (
          <AboutPage locale={locale} navigate={navigate} t={t} />
        ) : path === "/contact" ? (
          <ContactPage locale={locale} navigate={navigate} t={t} />
        ) : (
          <Catalog locale={locale} navigate={navigate} t={t} />
        )}
      </main>
      <StorefrontFooter
        navigationLabel={t("footer.navigation")}
        tagline={t("brand.tagline")}
        craft={t("footer.craft")}
        scope={t("footer.scope")}
        rights={t("footer.rights")}
        sections={footerSections}
        navigate={navigate}
      />
    </div>
  );
}
